// src/components/StudioScroll.jsx
import React, { useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FiArrowUpRight } from "react-icons/fi";

import { useLang } from "../context/LanguageContext";

gsap.registerPlugin(ScrollTrigger);

/* Structure only — every string comes from the dictionary so all four
   panels translate together. */
const PANELS = [
  { index: "01", key: "lashes", kicker: "studio.kickerStudio" },
  { index: "02", key: "brows", kicker: "studio.kickerStudio" },
  { index: "03", key: "nails", kicker: "studio.kickerStudio" },
  { index: "04", key: "shop", kicker: "studio.kickerShop", to: "/produktet" },
];

export default function StudioScroll() {
  const { t } = useLang();
  const root = useRef(null);
  const track = useRef(null);

  useLayoutEffect(() => {
    const el = root.current;
    const tr = track.current;
    if (!el || !tr) return;

    const mm = gsap.matchMedia();

    /* Horizontal pin only where there's room for it. On phones the panels
       stack and scroll normally — a pinned sideways scroll on a small screen
       fights the native gesture and feels broken. */
    mm.add(
      "(min-width: 1024px) and (prefers-reduced-motion: no-preference)",
      () => {
        const distance = () => tr.scrollWidth - window.innerWidth;

        const tween = gsap.to(tr, {
          x: () => -distance(),
          ease: "none",
          scrollTrigger: {
            trigger: el,
            start: "top top",
            end: () => `+=${distance()}`,
            pin: true,
            scrub: 1,
            invalidateOnRefresh: true,
            anticipatePin: 1,
          },
        });

        // each panel's content lifts in as it reaches centre
        gsap.utils.toArray(".studio-panel").forEach((panel) => {
          gsap.from(panel.querySelectorAll(".studio-fade"), {
            opacity: 0,
            y: 30,
            duration: 0.6,
            stagger: 0.08,
            ease: "power3.out",
            scrollTrigger: {
              trigger: panel,
              containerAnimation: tween,
              start: "left 72%",
              once: true,
            },
          });
        });

        return () => tween.kill();
      },
    );

    mm.add("(max-width: 1023px)", () => {
      gsap.utils.toArray(".studio-panel").forEach((panel) => {
        gsap.from(panel.querySelectorAll(".studio-fade"), {
          opacity: 0,
          y: 24,
          duration: 0.55,
          stagger: 0.07,
          ease: "power3.out",
          scrollTrigger: { trigger: panel, start: "top 78%", once: true },
        });
      });
    });

    return () => mm.revert();
  }, []);

  return (
    <Section ref={root} id="studio">
      <Track ref={track}>
        <Intro className="studio-panel">
          <IntroInner>
            <Kicker className="studio-fade">{t("studio.introKicker")}</Kicker>
            <IntroTitle className="studio-fade">
              {t("studio.introTitle1")}
              <br />
              {t("studio.introTitle2")}
            </IntroTitle>
            <IntroText className="studio-fade">
              {t("studio.introText")}
            </IntroText>
            <Hint className="studio-fade" aria-hidden="true">
              {t("studio.hint")}
            </Hint>
          </IntroInner>
        </Intro>

        {PANELS.map((p) => {
          const base = `studio.${p.key}`;
          const note = p.key === "nails" ? t(`${base}.note`) : null;

          return (
            <Panel key={p.index} className="studio-panel">
              <PanelInner>
                <Num className="studio-fade">{p.index}</Num>
                <Kicker className="studio-fade">{t(p.kicker)}</Kicker>
                <PanelTitle className="studio-fade">
                  {t(`${base}.title`)}
                </PanelTitle>
                <Lead className="studio-fade">{t(`${base}.lead`)}</Lead>
                <Body className="studio-fade">{t(`${base}.body`)}</Body>

                {note && <Note className="studio-fade">{note}</Note>}

                <Tags className="studio-fade">
                  {t(`${base}.tags`).map((tag) => (
                    <Tag key={tag}>{tag}</Tag>
                  ))}
                </Tags>

                <Cta className="studio-fade">
                  <PanelLink to={p.to || "/kontakt"}>
                    {p.to ? t("studio.seeShop") : t("studio.book")}{" "}
                    <FiArrowUpRight />
                  </PanelLink>
                </Cta>
              </PanelInner>
            </Panel>
          );
        })}
      </Track>
    </Section>
  );
}

/* ------------------------------------------------------------------ */

const Section = styled.section`
  background: ${({ theme }) => theme.color.bgDark};
  color: #fff;
  overflow: hidden;
`;

const Track = styled.div`
  display: flex;
  align-items: stretch;

  @media (max-width: 1023px) {
    flex-direction: column;
  }
`;

const PanelBase = styled.div`
  flex: 0 0 auto;
  width: 72vw;
  min-height: 100svh;
  display: flex;
  align-items: center;
  padding: 0 clamp(32px, 5vw, 90px);
  border-right: 1px solid rgba(255, 255, 255, 0.09);

  @media (max-width: 1023px) {
    width: 100%;
    min-height: auto;
    padding: 76px clamp(20px, 5vw, 48px);
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.09);
  }
`;

const Intro = styled(PanelBase)`
  width: 62vw;
`;

const IntroInner = styled.div`
  max-width: 46ch;
`;

const Panel = styled(PanelBase)``;

const PanelInner = styled.div`
  max-width: 44ch;
`;

const Kicker = styled.p`
  margin: 0 0 18px;
  font-size: ${({ theme }) => theme.size.xs};
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.color.brand};
  font-weight: ${({ theme }) => theme.weight.semi};
`;

const IntroTitle = styled.h2`
  margin: 0;
  color: #fff;
  font-size: clamp(2.6rem, 5.5vw, 5.6rem);
  line-height: 1.04;
  letter-spacing: -0.05em;
  font-weight: ${({ theme }) => theme.weight.medium};
`;

const IntroText = styled.p`
  margin: 30px 0 0;
  font-size: ${({ theme }) => theme.size.lg};
  color: rgba(255, 255, 255, 0.68);
`;

const Hint = styled.p`
  margin: 46px 0 0;
  font-size: 0.68rem;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.35);

  @media (max-width: 1023px) {
    display: none;
  }
`;

const Num = styled.span`
  display: block;
  margin-bottom: 26px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: clamp(3.4rem, 7vw, 6.5rem);
  line-height: 1.05;
  letter-spacing: -0.06em;
  color: rgba(255, 255, 255, 0.09);
`;

const PanelTitle = styled.h3`
  margin: 0;
  color: #fff;
  font-size: clamp(2.2rem, 4.4vw, 4.2rem);
  line-height: 1.06;
  letter-spacing: -0.045em;
  font-weight: ${({ theme }) => theme.weight.medium};
`;

const Lead = styled.p`
  margin: 24px 0 0;
  font-size: ${({ theme }) => theme.size.lg};
  color: rgba(255, 255, 255, 0.86);
`;

const Body = styled.p`
  margin: 16px 0 0;
  font-size: ${({ theme }) => theme.size.sm};
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.75;
`;

const Note = styled.p`
  display: inline-block;
  margin: 22px 0 0;
  padding: 7px 15px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: rgba(255, 255, 255, 0.07);
  font-size: ${({ theme }) => theme.size.xs};
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.7);
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 30px;
`;

const Tag = styled.span`
  padding: 7px 15px;
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid rgba(255, 255, 255, 0.16);
  font-size: ${({ theme }) => theme.size.xs};
  color: rgba(255, 255, 255, 0.75);
`;

const Cta = styled.div`
  margin-top: 36px;
`;

const PanelLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.28);
  font-size: ${({ theme }) => theme.size.sm};
  font-weight: ${({ theme }) => theme.weight.medium};
  color: #fff;
  transition:
    border-color 0.3s,
    gap 0.3s ${({ theme }) => theme.ease.out};

  svg {
    transition: transform 0.3s ${({ theme }) => theme.ease.out};
  }

  &:hover {
    border-color: ${({ theme }) => theme.color.brand};
    svg {
      transform: translate(3px, -3px);
    }
  }
`;
